import { API_PATH } from "@/constants/config";
import { request } from "@/helper/http";
import { getFileNameFromDisposition } from "@/helper/utility";
import { DictionaryFile, DictionaryFileId } from "@/types/dictionary";
import { APIQuery, APIResponse } from "@/types/global";
import { RequestOptions, downloadFromBinary } from "@cinnamon/design-system";
import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";

export const downloadDictionary = async (id: DictionaryFileId) => {
  const result = await request({
    url: `${API_PATH.DICTIONARIES}${id}/download/`,
    method: "get",
    responseType: "blob",
    shouldGetFullResponse: true,
  });

  const fileName = getFileNameFromDisposition(result.headers["content-disposition"]);
  downloadFromBinary(result.data, fileName);
};

export interface UploadDictionaryPayload {
  file: File;
  config?: RequestOptions;
}

export interface GetDictionaryPayload extends APIQuery {
  current?: boolean;
}

export const dictionaryApi = createApi({
  reducerPath: "dictionaryApi",
  baseQuery: fakeBaseQuery(),
  tagTypes: ["Dictionary"],
  endpoints: (builder) => ({
    uploadDictionary: builder.mutation<DictionaryFile, UploadDictionaryPayload>({
      queryFn: async (payload) => {
        try {
          const { file, config } = payload;
          const formData = new FormData();
          formData.append("file", file);

          const data = await request({
            url: API_PATH.DICTIONARIES,
            method: "post",
            data: formData,
            ...config,
          });

          return { data };
        } catch (error) {
          return { error };
        }
      },
      invalidatesTags: ["Dictionary"],
    }),
    getDictionary: builder.query<APIResponse<DictionaryFile>, GetDictionaryPayload>({
      queryFn: async (params) => {
        try {
          const data = await request({
            url: API_PATH.DICTIONARIES,
            method: "get",
            params,
          });

          return { data };
        } catch (error) {
          return { error };
        }
      },
      providesTags: ["Dictionary"],
    }),
  }),
});

export const { useGetDictionaryQuery, useUploadDictionaryMutation } = dictionaryApi;
